import { watch } from "vue";
import { useMediaQuery } from "@vueuse/core";

import { useGlobeStore } from "@stores/globe";
import { useGlobalStore } from "@stores/global";
import { useAchievementsStore } from "@stores/achievements";
import { useDataStore } from "@stores/data";
import { useTriviaStore } from "@stores/trivia";
import { useWorldStore } from "@stores/world";

import ThreeGlobe from "../utils/three/globe";

export function useGlobe() {
    const globeStore = useGlobeStore();
    const globalStore = useGlobalStore();
    const achievementsStore = useAchievementsStore();
    const dataStore = useDataStore();
    const triviaStore = useTriviaStore();
    const worldStore = useWorldStore();

    const globe = new ThreeGlobe();
    const isMobile = useMediaQuery("(max-width: 1024px)");

    const DOUBLE_CLICK_MS = 400;
    let lastClickName = null;
    let lastClickTime = 0;
    let _pendingCountryOnExplore = null;

    const GLOBE_PAGES = ["achievements", "settings"];

    globe.onCountryHover = (name, locked, continent) => {
        if (isMobile.value) return;
        const onExplore = globalStore.activePage === "explore";
        if (name) {
            globeStore.setHover(name, locked, continent);
            if (onExplore && !globeStore.panelDismissedByPan) {
                globalStore.showPanel("countryInfo");
            }
        } else {
            globeStore.clearHover();
            if (onExplore && !globeStore.selectedCountry) {
                globalStore.hidePanel("countryInfo");
            }
        }
    };

    function _applyExploreClick(name, locked, continent) {
        // Google Analytics Tracking - Clicked Location Pin
        if (typeof window.gtag === "function") {
            const pinnedCity = dataStore.pinnedCities.find((c) => c.name === name);
            const countryName = pinnedCity?.countryName ?? name;
            window.gtag("event", "clicked_location_pin", {
                location_name: name,
                country_name: countryName,
            });
        }

        const isProvince = !!dataStore.provinceByName.get(name);

        if (isProvince) {
            const firstCityName = globeStore.firstCityForProvince[name];
            if (firstCityName) {
                const firstCity = dataStore.pinnedCities.find(
                    (c) => c.name === firstCityName,
                );
                _applyExploreClick(
                    firstCityName,
                    firstCity?.locked ?? locked,
                    firstCity?.continent ?? continent,
                );
            }
            return;
        }

        globeStore.selectCountry(name, locked, continent);
        globalStore.showPanel("countryInfo");
        globalStore.showPanel("clips");

        const pinnedCity = dataStore.pinnedCities.find((c) => c.name === name);
        if (pinnedCity) {
            achievementsStore.visit("city-slicker", pinnedCity.name);
            achievementsStore.visit(
                "country-explorer",
                pinnedCity.countryCode ?? pinnedCity.countryName,
            );
            if (pinnedCity.speed) {
                achievementsStore.visit("shadowing-speed", pinnedCity.name);
            }
        } else {
            achievementsStore.visit("country-explorer", name);
        }
        if (continent) achievementsStore.visit("continent-hopper", continent);
    }

    globe.onCountryClick = (name, locked, continent) => {
        const now = Date.now();
        const isDoubleClick =
            name === lastClickName && now - lastClickTime < DOUBLE_CLICK_MS;
        lastClickName = name;
        lastClickTime = now;

        const activePage = globalStore.activePage;

        // Google Analytics Tracking - Clicked Country
        if (activePage === "explore" && typeof window.gtag === "function") {
            const pinnedCity = dataStore.pinnedCities.find((c) => c.name === name);
            const countryName = pinnedCity?.countryName ?? name;
            window.gtag("event", "clicked_country", {
                country_name: countryName,
            });
        }

        if (GLOBE_PAGES.includes(activePage)) {
            _pendingCountryOnExplore = { name, locked, continent };
            globalStore.setPage("explore");
            return;
        }

        globeStore.selectCountry(name, locked, continent);

        if (
            isDoubleClick &&
            activePage === "trivia" &&
            triviaStore.gameState === "playing" &&
            !triviaStore.hasCorrectGuess &&
            !triviaStore.loading
        ) {
            const match = worldStore.searchableCountries.find(
                (c) => c.name === name,
            );
            if (match) {
                triviaStore.submitGuess(match);
                globeStore.clearSelection();
                return;
            }
        }

        if (activePage !== "explore") return;

        _applyExploreClick(name, locked, continent);
    };

    globe.onPointerMissed = () => {
        globeStore.clearSelection();
        globalStore.hidePanel("countryInfo");
        globalStore.hidePanel("clips");
    };

    watch(
        () => dataStore.loaded,
        async (loaded) => {
            if (loaded) {
                await globe.startSetup(dataStore);
                globeStore.setProvinceCityMap(globe._firstCityForProvince);
            }
        },
        { immediate: true },
    );

    watch(
        () => globeStore.selectedCountry,
        (name) => globe.setSelectedCountry(name, globalStore.activePage),
    );

    watch(
        () => globeStore.showLocked,
        (show) => globe.setShowLocked(show),
    );

    watch(
        () => globeStore.showLines,
        (show) => globe.setShowLines(show),
    );

    function syncMarkers() {
        const playing = globalStore.mode === "playing";
        const page = globalStore.activePage;

        if (!playing) {
            globe.hideMarkers();
            globe.setInteractionEnabled(false);
            globe.setVoteMode(false);
            return;
        }

        if (page === "explore") {
            globe.showMarkers();
            globe.setInteractionEnabled(true);
            globe.setVoteMode(false);
        } else if (page === "achievements") {
            globe.showMarkers();
            globe.setInteractionEnabled(true);
            globe.setVoteMode(false);
        } else if (page === "settings") {
            globe.showMarkers();
            globe.setInteractionEnabled(true);
            globe.setVoteMode(false);
        } else if (page === "vote") {
            globe.hideMarkers();
            globe.setInteractionEnabled(true);
            globe.setVoteMode(true);
        } else if (page === "trivia" && triviaStore.gameState === "playing") {
            globe.hideMarkers();
            globe.setInteractionEnabled(true);
            globe.setVoteMode(true);
        } else {
            globe.hideMarkers();
            globe.setInteractionEnabled(false);
            globe.setVoteMode(false);
        }
    }

    watch(
        () => globalStore.mode,
        () => {
            globeStore.clearHover();
            globeStore.clearSelection();
            globalStore.hidePanel("countryInfo");
            globalStore.hidePanel("clips");
            syncMarkers();
        },
        { immediate: true },
    );
    watch(() => triviaStore.gameState, syncMarkers);

    watch(
        () => globalStore.activePage,
        (activePage) => {
            globe.setActivePage(activePage);
            globeStore.clearHover();
            globeStore.clearSelection();
            globalStore.hidePanel("countryInfo");
            globalStore.hidePanel("clips");
            syncMarkers();

            if (activePage === "explore" && _pendingCountryOnExplore) {
                const { name, locked, continent } = _pendingCountryOnExplore;
                _pendingCountryOnExplore = null;
                _applyExploreClick(name, locked, continent);
            }
        },
    );

    return { globe };
}
